import { SlideFrame, SlideHeader, Reveal } from "@/components/presentation/slides/SlideFrame";
import { MetricCard } from "@/components/primitives/MetricCard";
import { ChartPlaceholder } from "@/components/primitives/ChartPlaceholder";
import { Callout } from "@/components/primitives/Callout";
import { AnimatedNumber } from "@/components/primitives/AnimatedNumber";
import { cn } from "@/lib/cn";

/**
 * 06 · MetricsDashboardSlide
 *
 * For traction and business-performance chapters. A 4-up KPI strip, a big
 * chart, and a supporting insight callout. KPIs animate in (count-up) on
 * first reveal.
 */
const DEFAULT_KPIS = [
  { id: "kpi1", label: "Growth",     value: 128, suffix: "%", delta: "+12%", trend: "up",     hint: "MoM" },
  { id: "kpi2", label: "Customers",  value: 1284,             delta: "+86",   trend: "up",     hint: "Net new" },
  { id: "kpi3", label: "Retention",  value: 96,  suffix: "%", delta: "+1pp",  trend: "up",     hint: "Cohort" },
  { id: "kpi4", label: "Efficiency", value: 3.2, suffix: "x", delta: "0.0x",  trend: "neutral",hint: "LTV / CAC" },
];

export const MetricsDashboardSlide = ({
  eyebrow = "Traction",
  title = "Where the numbers are landing.",
  kpis = DEFAULT_KPIS,
  chartVariant = "line",
  insightTitle = "Reading the signal",
  insightBody = "One sentence that explains what these numbers actually mean.",
}) => {
  return (
    <SlideFrame testId="slide-metrics-dashboard" width="wide">
      <SlideHeader eyebrow={eyebrow} title={title} />

      <Reveal delay={2}>
        <div
          data-testid="metrics-kpi-grid"
          className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4"
        >
          {kpis.map((k) => (
            <MetricCard
              key={k.id}
              label={k.label}
              value={
                <AnimatedNumber
                  value={k.value}
                  format={(v) =>
                    `${Number.isInteger(k.value) ? Math.round(v).toLocaleString() : v.toFixed(1)}${k.suffix || ""}`
                  }
                />
              }
              delta={k.delta}
              trend={k.trend}
              hint={k.hint}
              className={cn("rounded-none border-0")}
              testId={`metrics-kpi-${k.id}`}
            />
          ))}
        </div>
      </Reveal>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Reveal delay={3} className="lg:col-span-2">
          <ChartPlaceholder variant={chartVariant} testId="metrics-chart" />
        </Reveal>
        <Reveal delay={4} className="lg:col-span-1">
          <Callout variant="success" title={insightTitle} testId="metrics-insight">
            {insightBody}
          </Callout>
        </Reveal>
      </div>
    </SlideFrame>
  );
};

export default MetricsDashboardSlide;
